// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Button } from '@loa/ui-components';
import type { ProposalSection } from '../../../types/proposal';
import { renderQuoteBackedSection } from './quoteBackedSectionRenderer';
import './PensionProposalBuilder.css';

export type QuoteBackedClientPreviewProps = Readonly<{
  proposalId: string;
  profileId?: string;
  /** The recommendation section's key for this proposal type (PENSION/INVESTMENT/PROTECTION_RECOMMENDATION). */
  recommendationSectionKey: string;
  sections: ProposalSection[];
  onSaved: (updated: ProposalSection) => void;
  onRemoved: (sectionKey: string) => void;
  onBackToEditing: () => void;
}>;

/**
 * The "Client view" side of the Advisor view <-> Client view toggle for the
 * generalised PENSION/FUND_INVESTMENT/PROTECTION sectioned builder — mirrors
 * {@link BreakthroughClientPreview}'s pattern exactly: a read-only,
 * top-to-bottom document rendering of every section in `sortOrder`.
 *
 * Only the BACKEND `proposal_sections` are rendered here — never the
 * synthetic QUOTE_REQUEST/QUOTE_OPTIONS rail steps that
 * {@link QuoteBackedProposalBuilder} hosts for the v1 wizard flow. Those are
 * advisor-only tooling for building the recommendation and are not part of
 * the client-facing document, so `sections` (state that already excludes
 * them) is passed straight through with no filtering needed here.
 *
 * Dispatched through the same {@link renderQuoteBackedSection} the editable
 * builder uses, with `readOnly` forced on — no rail, no edit controls, no
 * Save buttons, no "Open quote options" deep-link. Switching back to Advisor
 * view via {@link onBackToEditing} loses no unsaved edits: the editable pane
 * remains the source of truth and this component never mutates section
 * state on its own.
 */
export function QuoteBackedClientPreview({
  proposalId,
  profileId,
  recommendationSectionKey,
  sections,
  onSaved,
  onRemoved,
  onBackToEditing,
}: QuoteBackedClientPreviewProps) {
  const ordered = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="pension-builder__client-preview" data-testid="quote-backed-client-preview">
      {ordered.length === 0 && (
        <p
          className="pension-builder__client-preview-empty"
          data-testid="quote-backed-client-preview-empty"
        >
          The client&apos;s document appears here once you&apos;ve requested quotes and started
          building the proposal.
        </p>
      )}
      {ordered.map((section) => (
        <section
          key={section.sectionKey}
          className="pension-builder__content pension-builder__client-preview-section"
          data-testid={`quote-backed-client-preview-section-${section.sectionKey}`}
        >
          <h2 className="pension-builder__client-preview-section-title">{section.title}</h2>
          {renderQuoteBackedSection(
            section,
            proposalId,
            profileId,
            onSaved,
            onRemoved,
            undefined,
            recommendationSectionKey,
            true,
          )}
        </section>
      ))}

      <div className="pension-builder__client-preview-footer">
        <Button
          variant="outlined"
          onClick={onBackToEditing}
          data-testid="quote-backed-client-preview-back-btn"
        >
          Back to editing
        </Button>
      </div>
    </div>
  );
}

export default QuoteBackedClientPreview;
